import { Grid, Paper } from "@material-ui/core";
import { useGetAllActivityTypesQuery } from "features/activity/activitySlice";
import { useMemo } from "react";
import { Bar, BarChart, LabelList, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { arrayToObject } from "utils/object-util";
import { GraphLoader } from "../GraphLoader";
import { vendorDashStyle } from "../style";
import ServiceListDialog from "./ServiceListDialog";

export const ServicesGraphWidget = ({ activities, isLoading }) => {
    const classes = vendorDashStyle();
    let { data: activityTypes, error: activityTypesError, isLoading: isLoadingActivityTypes } = useGetAllActivityTypesQuery();

    const activityTypeMap = useMemo(() => arrayToObject(activityTypes), [activityTypes]);

    const chartData = useMemo(() => {
        if (!activities || !activityTypes) return [];
        // Helper to get month string (e.g. 'FEB 2025') and sortable key
        const getMonthInfo = (dateStr) => {
            const d = new Date(dateStr);
            const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
            return {
                label: `${months[d.getMonth()]} ${d.getFullYear()}`,
                sortKey: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
            };
        };
        // Group by month
        const grouped = {};
        activities.forEach(act => {
            const { label, sortKey } = getMonthInfo(act.activityDate);
            if (!grouped[sortKey]) grouped[sortKey] = { month: label, sortKey };
            const typeId = act.activityTypeId;
            grouped[sortKey][typeId] = (grouped[sortKey][typeId] || 0) + 1;
        });
        // Build chart data array, only include non-zero bars, use activity type name as key
        return Object.values(grouped).map(row => {
            const out = { month: row.month, sortKey: row.sortKey };
            Object.entries(row).forEach(([typeId, count]) => {
                if (typeId !== 'month' && typeId !== 'sortKey' && count > 0 && activityTypeMap[typeId]) {
                    out[activityTypeMap[typeId].name] = count;
                }
            });
            return out;
        }).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    }, [activities, activityTypes, activityTypeMap]);

    const blueShades = [
        '#1565c0', '#1976d2', '#1e88e5', '#2196f3', '#42a5f5', '#64b5f6', '#90caf9', '#bbdefb', '#e3f2fd'
    ];

    const activityTypeNames = activityTypes?.map(t => t?.name) ?? [];

    const tickStyle = {
        color: 'darkGray',
        fontSize: '0.7em',
        fontWeight: '600',
    }

    return (
        <Paper className={classes.card}>
            <Grid container direction="column" spacing={1}>
                <Grid container item className={classes.widgetTitle} justifyContent="space-between">
                    <Grid item> Services</Grid>
                    <Grid item><ServiceListDialog activities={activities} /></Grid>
                </Grid>
                <Grid item style={{ height: 350, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                            <XAxis
                                dataKey="month"
                                tickLine={false}
                                tick={tickStyle}
                                interval={0}
                            />
                            <YAxis
                                allowDecimals={false}
                                tickLine={false}
                                tick={tickStyle}
                            />
                            <Tooltip />
                            <Legend
                                iconType='circle'
                                layout="horizontal"
                                verticalAlign="bottom"
                                iconSize={10}
                                formatter={(value, entry, index) => <span style={{ fontSize: '0.8em' }}>{value}</span>} />
                            {activityTypeNames.map((name, idx) => (
                                <Bar key={name}
                                    dataKey={name}
                                    stackId={null}
                                    fill={blueShades[idx % blueShades.length]}
                                >
                                    <LabelList dataKey={name} position="inside" style={{ fill: 'white', fontSize: '0.8em' }} />
                                </Bar>
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </Grid>
            </Grid>
            <GraphLoader loading={isLoading} />
        </Paper>
    )
}